import { useCallback, useEffect, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";
import {
  type BrebCustomKeyAvailability,
  BrebReceivingKeyService,
  normalizeCustomBrebKey,
} from "@/shared/services/payments/brebReceivingKey.service";

const DEBOUNCE_MS = 500;

interface Outcome {
  result: BrebCustomKeyAvailability | null;
  error: string | null;
}

export type CustomKeyCheck =
  | { status: "empty" }
  | { status: "invalid" }
  | { status: "checking"; keyValue: string }
  | { status: "done"; keyValue: string; result: BrebCustomKeyAvailability }
  | { status: "error"; keyValue: string; message: string };

export interface UseCustomBrebKeyAvailability {
  check: CustomKeyCheck;
  /** Forgets the answer for the current value and asks again (after UNKNOWN, an error or a refused assign). */
  recheck: () => void;
}

// One request per pause in typing; a value already answered is not asked again until `recheck`.
export function useCustomBrebKeyAvailability(input: string): UseCustomBrebKeyAvailability {
  const keyValue = normalizeCustomBrebKey(input);
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({});
  const latestRef = useRef(0);

  useEffect(() => {
    if (!keyValue || outcomes[keyValue]) return;
    const requestId = ++latestRef.current;
    const timer = window.setTimeout(() => {
      BrebReceivingKeyService.checkCustomAvailability(keyValue).then(
        (result) => {
          if (requestId !== latestRef.current) return;
          setOutcomes((previous) => ({ ...previous, [keyValue]: { result, error: null } }));
        },
        (error: unknown) => {
          if (requestId !== latestRef.current) return;
          setOutcomes((previous) => ({
            ...previous,
            [keyValue]: { result: null, error: HttpApiService.getErrorMessage(error) },
          }));
        },
      );
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [keyValue, outcomes]);

  const recheck = useCallback(() => {
    if (!keyValue) return;
    setOutcomes((previous) => {
      const next = { ...previous };
      delete next[keyValue];
      return next;
    });
  }, [keyValue]);

  if (!input.trim()) return { check: { status: "empty" }, recheck };
  if (!keyValue) return { check: { status: "invalid" }, recheck };
  const outcome = outcomes[keyValue];
  if (!outcome) return { check: { status: "checking", keyValue }, recheck };
  if (outcome.result) return { check: { status: "done", keyValue, result: outcome.result }, recheck };
  return { check: { status: "error", keyValue, message: outcome.error ?? "" }, recheck };
}
